import { useSyncExternalStore } from "react";

/**
 * Maintenance mode (#459). While it is on, the backend answers members' API
 * requests with 503 `{detail, maintenance: true, until}` (`core/maintenance.py`);
 * the axios interceptor records that here and `App` shows `MaintenanceScreen`
 * instead of the portal. Mirrors `frontend/src/lib/maintenance.ts`.
 */

export interface MaintenanceInfo {
  message: string;
  until: string | null;
}

let current: MaintenanceInfo | null = null;
const listeners = new Set<() => void>();

export const setMaintenance = (info: MaintenanceInfo | null) => {
  if (current?.message === info?.message && current?.until === info?.until && !!current === !!info) return;
  current = info;
  listeners.forEach((listener) => listener());
};

export const getMaintenance = () => current;

export const useMaintenance = () =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getMaintenance,
    getMaintenance,
  );

/** The 503 body of maintenance mode, or null for any other error. */
export const maintenanceFromError = (error: unknown): MaintenanceInfo | null => {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
  const data = response?.data as { maintenance?: unknown; detail?: unknown; until?: unknown } | undefined;
  if (response?.status !== 503 || data?.maintenance !== true) return null;
  return {
    message: typeof data.detail === "string" ? data.detail : "",
    until: typeof data.until === "string" ? data.until : null,
  };
};

/** "18:30" (or a date and time) in the member's locale, or null for a missing / past time. */
export const formatUntil = (until: string | null): string | null => {
  if (!until) return null;
  const at = new Date(until);
  if (Number.isNaN(at.getTime()) || at.getTime() <= Date.now()) return null;
  const sameDay = at.toDateString() === new Date().toDateString();
  return at.toLocaleString(
    undefined,
    sameDay
      ? { hour: "2-digit", minute: "2-digit" }
      : { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" },
  );
};
